import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
} from 'react-icons/fa'
import { authApi } from '../../lib/auth'
import { useVideoAnalytics, type VideoTracking } from './useVideoAnalytics'

/**
 * Where the media comes from.
 *  - `hls`  — adaptive HLS in Cloudflare R2 behind the video.gazar.dev Worker
 *             (course lessons + the course trailer). A gated source
 *             (`requiresToken`) first POSTs for a playback token — which also
 *             sets the `.gazar.dev` `pb` cookie the Worker checks — before
 *             hls.js (or Safari's native HLS) streams it.
 *  - `file` — a single progressive-download file (e.g. an article's hero mp4 in
 *             the public assets bucket). Handed straight to the <video> element;
 *             hls.js is never loaded.
 */
export type VideoPlayerSource =
  | { type: 'hls'; src: string; requiresToken?: boolean; courseSlug?: string }
  | { type: 'file'; src: string }

interface VideoPlayerProps {
  source: VideoPlayerSource
  /** Frame shown before playback starts (usually the article/course cover). */
  poster?: string | null
  /** CSS aspect-ratio (e.g. "16 / 9", "1644 / 1080"). Defaults to 16:9. */
  aspectRatio?: string | null
  /**
   * Identity of the video for OpenPanel reporting. Omit to play untracked; the
   * player works identically either way.
   */
  track?: VideoTracking
}

const SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const

function fmtTime(t: number): string {
  if (!Number.isFinite(t) || t < 0) t = 0
  const m = Math.floor(t / 60)
  const s = Math.floor(t % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Brand-styled video player, shared by the course landing trailer, course
 * lessons, and article heroes. The native controls are replaced with a custom
 * control bar so the player matches the brand (flat colours, borders, no
 * shadows): a centre play button, a scrubber with a buffered indicator, time,
 * mute/volume, playback speed and fullscreen, plus click-to-play and keyboard
 * shortcuts. The source loader is the only part that differs between an HLS and
 * a progressive-file source; everything below the load effect is source-blind.
 */
export default function VideoPlayer({ source, poster, aspectRatio, track }: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const scrubRef = useRef<HTMLDivElement>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [error, setError] = useState('')
  const [playing, setPlaying] = useState(false)
  const [started, setStarted] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [buffered, setBuffered] = useState(0)
  const [buffering, setBuffering] = useState(false)
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [rate, setRate] = useState(1)
  const [rateOpen, setRateOpen] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [controlsVisible, setControlsVisible] = useState(true)

  // Discriminated-union fields pulled out to primitives so the load effect has a
  // stable dependency list (the `source` object identity changes every render).
  const srcType = source.type
  const src = source.src
  const requiresToken = source.type === 'hls' ? !!source.requiresToken : false
  const courseSlug = source.type === 'hls' ? source.courseSlug : undefined

  // ── Report playback to OpenPanel ────────────────────────────────────────────
  useVideoAnalytics(videoRef, track)

  // ── Load the source ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    let hls: { destroy: () => void } | undefined
    setError('')

    ;(async () => {
      try {
        const el = videoRef.current
        if (!el) return

        // Progressive file: nothing to negotiate, hand it to the element.
        if (srcType === 'file') {
          el.src = src
          return
        }

        // HLS: mint a playback token for gated sources, then stream.
        if (requiresToken && courseSlug) {
          await authApi.playbackToken(courseSlug)
        }
        if (cancelled) return

        const { default: Hls } = await import('hls.js')
        if (cancelled) return

        if (Hls.isSupported()) {
          const instance = new Hls({
            xhrSetup: (xhr) => {
              xhr.withCredentials = requiresToken
            },
          })
          hls = instance
          instance.on(Hls.Events.ERROR, (_evt, data) => {
            if (!data.fatal || cancelled) return
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) instance.startLoad()
            else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) instance.recoverMediaError()
            else setError('This video could not be loaded. Try refreshing the page.')
          })
          instance.loadSource(src)
          instance.attachMedia(el)
        } else {
          // Safari plays HLS natively.
          el.src = src
        }
      } catch {
        if (!cancelled) setError('This video is unavailable right now. Try refreshing the page.')
      }
    })()

    return () => {
      cancelled = true
      hls?.destroy()
    }
  }, [srcType, src, requiresToken, courseSlug])

  // ── Mirror the media element state into React ───────────────────────────────
  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    const onTime = () => setCurrent(el.currentTime)
    const onDur = () => setDuration(el.duration || 0)
    const onProgress = () => {
      try {
        if (el.buffered.length) setBuffered(el.buffered.end(el.buffered.length - 1))
      } catch {
        /* buffered can throw before data arrives */
      }
    }
    const onPlay = () => {
      setPlaying(true)
      setStarted(true)
    }
    const onPause = () => setPlaying(false)
    const onWaiting = () => setBuffering(true)
    const onPlaying = () => setBuffering(false)
    const onVolume = () => {
      setMuted(el.muted)
      setVolume(el.volume)
    }
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('durationchange', onDur)
    el.addEventListener('loadedmetadata', onDur)
    el.addEventListener('progress', onProgress)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('waiting', onWaiting)
    el.addEventListener('playing', onPlaying)
    el.addEventListener('volumechange', onVolume)
    return () => {
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('durationchange', onDur)
      el.removeEventListener('loadedmetadata', onDur)
      el.removeEventListener('progress', onProgress)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('waiting', onWaiting)
      el.removeEventListener('playing', onPlaying)
      el.removeEventListener('volumechange', onVolume)
    }
  }, [])

  // ── Fullscreen state follows the browser ────────────────────────────────────
  useEffect(() => {
    const onFs = () => setFullscreen(document.fullscreenElement === containerRef.current)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  const revealControls = useCallback(() => {
    setControlsVisible(true)
    if (hideTimer.current) clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => {
      const el = videoRef.current
      if (el && !el.paused) setControlsVisible(false)
    }, 2600)
  }, [])

  const togglePlay = useCallback(() => {
    const el = videoRef.current
    if (!el) return
    if (el.paused) el.play().catch(() => {})
    else el.pause()
  }, [])

  const seekTo = useCallback(
    (t: number) => {
      const el = videoRef.current
      if (!el) return
      el.currentTime = Math.max(0, Math.min(duration || el.duration || 0, t))
    },
    [duration],
  )

  const scrubFromClientX = useCallback(
    (clientX: number) => {
      const bar = scrubRef.current
      const dur = duration || videoRef.current?.duration || 0
      if (!bar || !dur) return
      const rect = bar.getBoundingClientRect()
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      seekTo(pct * dur)
    },
    [duration, seekTo],
  )

  const onScrubPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      scrubFromClientX(e.clientX)
      const move = (ev: PointerEvent) => scrubFromClientX(ev.clientX)
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    [scrubFromClientX],
  )

  const toggleMute = useCallback(() => {
    const el = videoRef.current
    if (el) el.muted = !el.muted
  }, [])

  const changeVolume = useCallback((v: number) => {
    const el = videoRef.current
    if (!el) return
    el.volume = v
    el.muted = v === 0
  }, [])

  const changeRate = useCallback((r: number) => {
    const el = videoRef.current
    if (el) el.playbackRate = r
    setRate(r)
    setRateOpen(false)
  }, [])

  const toggleFullscreen = useCallback(() => {
    const c = containerRef.current
    if (!c) return
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
    else c.requestFullscreen?.().catch(() => {})
  }, [])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const el = videoRef.current
      if (!el) return
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault()
          togglePlay()
          break
        case 'ArrowLeft':
          e.preventDefault()
          seekTo(el.currentTime - 5)
          break
        case 'ArrowRight':
          e.preventDefault()
          seekTo(el.currentTime + 5)
          break
        case 'ArrowUp':
          e.preventDefault()
          changeVolume(Math.min(1, el.volume + 0.1))
          break
        case 'ArrowDown':
          e.preventDefault()
          changeVolume(Math.max(0, el.volume - 0.1))
          break
        case 'm':
          toggleMute()
          break
        case 'f':
          toggleFullscreen()
          break
        default:
          return
      }
      revealControls()
    },
    [togglePlay, seekTo, changeVolume, toggleMute, toggleFullscreen, revealControls],
  )

  const pct = duration ? (current / duration) * 100 : 0
  const bufPct = duration ? (buffered / duration) * 100 : 0
  // Keep the control bar out of the pristine start frame: before the first play
  // the poster + centre button carry the whole surface. Once started, the bar
  // shows on hover or whenever paused, like any player.
  const barVisible = started && (controlsVisible || !playing)

  // Fullscreen ignores the requested ratio and fills the screen. Otherwise a
  // custom ratio wins via inline style; without one we fall back to 16:9.
  const useCustomRatio = !fullscreen && !!aspectRatio
  const frameClass = fullscreen ? 'h-screen rounded-none' : `rounded-smooth-lg ${aspectRatio ? '' : 'aspect-video'}`

  return (
    <figure className="mb-4">
      <div
        ref={containerRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseMove={revealControls}
        onMouseLeave={() => playing && setControlsVisible(false)}
        style={useCustomRatio ? { aspectRatio: aspectRatio! } : undefined}
        className={`group relative w-full overflow-hidden border border-border-subtle bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky ${frameClass}`}
      >
        <video
          ref={videoRef}
          playsInline
          poster={poster ?? undefined}
          onClick={togglePlay}
          className="h-full w-full object-contain"
        />

        {/* Buffering spinner */}
        {buffering && started && !error && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          </div>
        )}

        {/* Start screen — the pristine pre-play state. Poster behind, a large
            frosted play button, and a duration pill. No control bar yet. */}
        {!error && !started && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="Play"
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-brand-navy/20 transition-colors duration-150 hover:bg-brand-navy/30"
          >
            <span className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-white/50 bg-brand-navy/70 text-white backdrop-blur-sm transition-colors duration-150 group-hover:border-white group-hover:bg-brand-navy/90">
              <FaPlay className="ml-1.5 text-2xl" aria-hidden="true" />
            </span>
            {duration > 0 && (
              <span className="rounded-full border border-white/40 bg-brand-navy/60 px-3 py-1 font-heading text-caption uppercase tracking-wide text-white backdrop-blur-sm">
                Watch <span className="text-white/50">·</span> {fmtTime(duration)}
              </span>
            )}
          </button>
        )}

        {/* Mid-play pause overlay — a lighter centre button once playback has begun. */}
        {!error && started && !playing && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="Play"
            className="absolute inset-0 z-10 flex items-center justify-center bg-black/10 transition-colors duration-150 hover:bg-black/20"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/25 bg-brand-navy/85 text-white transition-colors duration-150 group-hover:bg-brand-navy">
              <FaPlay className="ml-1 text-xl" aria-hidden="true" />
            </span>
          </button>
        )}

        {/* Control bar */}
        {!error && (
          <div
            className={`absolute inset-x-0 bottom-0 z-20 bg-brand-navy/85 px-3 pb-2.5 pt-2 transition-opacity duration-200 ${
              barVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            {/* Scrubber */}
            <div
              ref={scrubRef}
              onPointerDown={onScrubPointerDown}
              role="slider"
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={Math.round(duration)}
              aria-valuenow={Math.round(current)}
              tabIndex={-1}
              className="group/scrub relative mb-2 h-1.5 cursor-pointer rounded-full bg-white/20"
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-white/30"
                style={{ width: `${bufPct}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-brand-sky"
                style={{ width: `${pct}%` }}
              />
              <div
                className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white bg-brand-sky opacity-0 transition-opacity duration-150 group-hover/scrub:opacity-100"
                style={{ left: `${pct}%` }}
              />
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 text-white">
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Play'}
                className="flex h-8 w-8 items-center justify-center rounded-smooth text-white/90 transition-colors duration-150 hover:text-white"
              >
                {playing ? <FaPause aria-hidden="true" /> : <FaPlay className="ml-0.5" aria-hidden="true" />}
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
                  className="flex h-8 w-8 items-center justify-center text-white/90 transition-colors duration-150 hover:text-white"
                >
                  {muted || volume === 0 ? (
                    <FaVolumeMute aria-hidden="true" />
                  ) : (
                    <FaVolumeUp aria-hidden="true" />
                  )}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(e) => changeVolume(Number(e.target.value))}
                  aria-label="Volume"
                  className="hidden h-1 w-16 cursor-pointer sm:block"
                  style={{ accentColor: '#4A90D9' }}
                />
              </div>

              <span className="font-heading text-caption tabular-nums text-white/80">
                {fmtTime(current)} <span className="text-white/40">/</span> {fmtTime(duration)}
              </span>

              <div className="ml-auto flex items-center gap-1">
                {/* Playback speed */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setRateOpen((o) => !o)}
                    aria-label="Playback speed"
                    aria-expanded={rateOpen}
                    className="flex h-8 min-w-[2.25rem] items-center justify-center rounded-smooth px-1.5 font-heading text-caption tabular-nums text-white/90 transition-colors duration-150 hover:text-white"
                  >
                    {rate}×
                  </button>
                  {rateOpen && (
                    <div className="absolute bottom-full right-0 mb-2 overflow-hidden rounded-smooth border border-white/15 bg-brand-navy">
                      {SPEEDS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => changeRate(s)}
                          className={`block w-full px-4 py-1.5 text-right font-heading text-caption tabular-nums transition-colors duration-150 hover:bg-white/10 ${
                            s === rate ? 'text-brand-sky' : 'text-white/80'
                          }`}
                        >
                          {s}×
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={toggleFullscreen}
                  aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  className="flex h-8 w-8 items-center justify-center text-white/90 transition-colors duration-150 hover:text-white"
                >
                  {fullscreen ? <FaCompress aria-hidden="true" /> : <FaExpand aria-hidden="true" />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {error && <figcaption className="mt-3 font-body text-body-sm text-red-600">{error}</figcaption>}
    </figure>
  )
}
